import { SelectOption } from "@via/utils/interfaces";

// A single column filter: the column id and the value it is filtered on
export type FilterItem = { id: string; value: unknown };

// Field type enum
export enum FieldTypeEnum {
  String = "String",
  Number = "Number",
  Boolean = "Boolean",
  Date = "Date",
  Array = "Array",
  Mixed = "Mixed",
}

// Operator enum
export enum OperatorEnum {
  Equals = "eq",
  NotEquals = "ne",
  GreaterThan = "gt",
  LessThan = "lt",
  GreaterThanOrEqual = "gte",
  LessThanOrEqual = "lte",
  Contains = "contains",
  StartsWith = "startsWith",
  EndsWith = "endsWith",
}

// Field type interface
export interface FieldType {
  name: string;
  label: string;
  type: FieldTypeEnum;
  hasNamingPattern?: boolean;
}

// Operator option interface
export interface OperatorOption extends SelectOption {
  value: OperatorEnum;
  label: string;
}

// Input component types
export enum InputComponentType {
  Text = "text",
  Number = "number",
  Boolean = "boolean",
  Date = "date",
}

// Field type to operators mapping
export const FIELD_TYPE_OPERATORS: Record<FieldTypeEnum, OperatorEnum[]> = {
  [FieldTypeEnum.String]: [
    OperatorEnum.Equals,
    OperatorEnum.NotEquals,
    OperatorEnum.Contains,
    OperatorEnum.StartsWith,
    OperatorEnum.EndsWith,
  ],
  [FieldTypeEnum.Number]: [
    OperatorEnum.Equals,
    OperatorEnum.NotEquals,
    OperatorEnum.GreaterThan,
    OperatorEnum.LessThan,
    OperatorEnum.GreaterThanOrEqual,
    OperatorEnum.LessThanOrEqual,
  ],
  [FieldTypeEnum.Boolean]: [OperatorEnum.Equals, OperatorEnum.NotEquals],
  [FieldTypeEnum.Date]: [
    OperatorEnum.Equals,
    OperatorEnum.NotEquals,
    OperatorEnum.GreaterThan,
    OperatorEnum.LessThan,
    OperatorEnum.GreaterThanOrEqual,
    OperatorEnum.LessThanOrEqual,
  ],
  [FieldTypeEnum.Array]: [OperatorEnum.Contains],
  [FieldTypeEnum.Mixed]: [OperatorEnum.Contains],
};

// Field type to input component mapping
export const FIELD_TYPE_INPUT_COMPONENT: Record<
  FieldTypeEnum,
  InputComponentType
> = {
  [FieldTypeEnum.String]: InputComponentType.Text,
  [FieldTypeEnum.Number]: InputComponentType.Number,
  [FieldTypeEnum.Boolean]: InputComponentType.Boolean,
  [FieldTypeEnum.Date]: InputComponentType.Date,
  [FieldTypeEnum.Array]: InputComponentType.Text,
  [FieldTypeEnum.Mixed]: InputComponentType.Text,
};

// Operator labels mapping
export const OPERATOR_LABELS: Record<OperatorEnum, string> = {
  [OperatorEnum.Equals]: "Equals",
  [OperatorEnum.NotEquals]: "Not Equals",
  [OperatorEnum.GreaterThan]: "Greater Than",
  [OperatorEnum.LessThan]: "Less Than",
  [OperatorEnum.GreaterThanOrEqual]: "Greater Than or Equal",
  [OperatorEnum.LessThanOrEqual]: "Less Than or Equal",
  [OperatorEnum.Contains]: "Contains",
  [OperatorEnum.StartsWith]: "Starts With",
  [OperatorEnum.EndsWith]: "Ends With",
};

// Boolean options for boolean input
export const BOOLEAN_OPTIONS: SelectOption[] = [
  { label: "True", value: true },
  { label: "False", value: false },
];

// Validation rules for different input types
export const INPUT_VALIDATION_RULES = {
  [InputComponentType.Text]: {
    required: "Value is required",
  },
  [InputComponentType.Number]: {
    required: "Value is required",
    pattern: {
      value: /^-?\d*\.?\d+$/,
      message: "Please enter a valid number",
    },
  },
  [InputComponentType.Boolean]: {
    required: "Value is required",
  },
  [InputComponentType.Date]: {
    required: "Value is required",
  },
};
